import React, { useContext, useEffect, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import ListItemButton from '@wix/da-ds/pkg/List/ListItem/dsPresets/ListItemButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import SafeBrowsingMode from '@wix/da-ds/pkg/Illustrations/SafeBrowsingMode';
import StandardBrowsingMode from '@wix/da-ds/pkg/Illustrations/StandardBrowsingMode';
import MatureBrowsingMode from '@wix/da-ds/pkg/Illustrations/MatureBrowsingMode';
import SisuForm from '../../SignupSignin/SisuForm';
import SisuPage from '../SisuPage';
import SelectionCard from './SelectionCard';

import s from './ContentFilterPage.scss';

const CONTENT_FILTER_OPTIONS = ['safe', 'standard', 'mature'];
type contentFilterOptions = (typeof CONTENT_FILTER_OPTIONS)[number];
const UNDER_18_CONTENT_FILTER_OPTIONS = ['safe', 'standard'];
const DEFAULT_CONTENT_FILTER = 'standard';

const ILLUSTRATIONS = {
  safe: SafeBrowsingMode,
  standard: StandardBrowsingMode,
  mature: MatureBrowsingMode,
};

export interface Props {
  authMode: string;
  referer?: string;
  isUserOlderThan18: boolean;
  clickContinue(authMode): void;
  screenLoaded(authMode): void;
}

export const ContentFilterPage: React.FC<Props> = ({
  authMode,
  isUserOlderThan18,
  clickContinue,
  screenLoaded,
}) => {
  const isMobile = useContext(MobileContext);
  const { t } = useTranslation();

  useEffect(() => {
    screenLoaded(authMode);
  }, [screenLoaded, authMode]);

  const [selectedFilter, selectFilter] = useState<contentFilterOptions>(
    DEFAULT_CONTENT_FILTER
  );
  const filterOptions = isUserOlderThan18
    ? CONTENT_FILTER_OPTIONS
    : UNDER_18_CONTENT_FILTER_OPTIONS;

  return (
    <SisuPage
      className={s['page']}
      bodyClassName={s['page-body']}
      cardClassName={s['page-card']}
      title={t('contentFilter.page.title')}
      withHero={false}
      withLogo={false}
      isUnskippable
    >
      <SisuForm
        className={classnames(s['form'], 'theme-light', 'forced-theme-light')}
        action="/_sisu/do/save_content_filter"
        method="post"
      >
        <header className={s['header']}>
          <Heading className={s['title']}>{t('contentFilter.title')}</Heading>
          <p className={s['subtitle']}>{t('contentFilter.subtitle')}</p>
        </header>

        <div
          className={s['middle']}
          role="radiogroup"
          aria-label={t('contentFilter.filterSelection.a11y.label')}
        >
          {filterOptions.map(value => {
            const Illustration = ILLUSTRATIONS[value];
            const illustration = (
              <Illustration className={s['illustration']} aria-hidden="true" />
            );
            const title = t(`contentFilter.filterSelection.title.${value}`);
            const subtitle = t(
              `contentFilter.filterSelection.subtitle.${value}`
            );
            const isSelected = value === selectedFilter;
            return isMobile ? (
              <ListItemButton
                className={classnames(
                  s['filter-selector'],
                  isSelected && s['selected']
                )}
                key={value}
                prefix={illustration}
                label={title}
                isLabelBold
                subLabel={subtitle}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => selectFilter(value)}
              />
            ) : (
              <SelectionCard
                className={s['filter-selector']}
                key={value}
                illustration={illustration}
                title={title}
                subtitle={subtitle}
                type="button"
                role="radio"
                isSelected={isSelected}
                aria-checked={isSelected}
                onClick={() => selectFilter(value)}
              />
            );
          })}

          <input type="hidden" name="content_filter" value={selectedFilter} />
        </div>

        <footer className={s['footer']}>
          <BrandedButton
            key="submit"
            className={s['submit-button']}
            size="large"
            variant="brand-green"
            type="submit"
            disabled={!selectedFilter}
            onClick={() => clickContinue(authMode)}
          >
            {t('contentFilter.continue')}
          </BrandedButton>
        </footer>
      </SisuForm>
    </SisuPage>
  );
};
export default ContentFilterPage;
